import type { FinanceHolding, InvestmentPerformanceAccount, InvestmentPerformancePosition } from "./financeConnections";
export function holdingValue(row: Pick<FinanceHolding,"quantity"|"marketPrice"|"institutionPrice"|"institutionValue"|"dayChangeStatus">): number | null {
  if(typeof row.quantity!=="number"||!Number.isFinite(row.quantity)||row.dayChangeStatus==="Latest price unavailable")return null;
  if(row.quantity===0)return 0;
  const price=[row.marketPrice,row.institutionPrice].find(value=>typeof value==="number"&&Number.isFinite(value)&&value>0);
  const value=price!==undefined?price*row.quantity:row.institutionValue;
  return typeof value==="number"&&Number.isFinite(value)&&(price!==undefined||value>0)?value:null;
}
export function weeklyRankings(accounts: InvestmentPerformanceAccount[]) {
  return accounts.flatMap(account=>account.positions.map(position=>({...position,accountId:account.accountId,change:positionGain(account,position)}))).filter(row=>row.change!==null);
}
export function todayPerformance(row: Pick<FinanceHolding,"dayChange"|"dayChangePercent"|"dayChangeStatus">): {change:number;percent:number|null} {
  if(row.dayChangeStatus!=="current"||typeof row.dayChange!=="number"||!Number.isFinite(row.dayChange))return {change:NaN,percent:null};
  return {change:row.dayChange,percent:typeof row.dayChangePercent==="number"&&Number.isFinite(row.dayChangePercent)?row.dayChangePercent:null};
}
export function weekLabel(day:string){
  const date=new Date(day+"T12:00:00Z");
  const thursday=new Date(date);thursday.setUTCDate(date.getUTCDate()+3-(date.getUTCDay()+6)%7);
  const yearStart=new Date(Date.UTC(thursday.getUTCFullYear(),0,1));
  const number=Math.ceil(((thursday.getTime()-yearStart.getTime())/86400000+1)/7);
  return `Week ${number} · ${date.toLocaleDateString("en-US",{month:"short",day:"numeric",timeZone:"UTC"})}`;
}
export function positionProblems(account:InvestmentPerformanceAccount,position:InvestmentPerformancePosition){
  const local=new Set(account.positions.flatMap(row=>row.issues));
  // Unknown trade dates/sync gaps can affect every holding in that account.
  // A missing quote on one row cannot invalidate another row's calculation.
  return [...new Set([...position.issues,...(account.accountIssues??account.issues.filter(issue=>!local.has(issue)))])];
}
export function positionGain(account:InvestmentPerformanceAccount,position:InvestmentPerformancePosition){return positionProblems(account,position).length?null:position.change;}
export function issueLabel(issue:string){
  if(issue==="Prior-week closing price unavailable")return "Friday price unavailable";
  if(issue==="Awaiting current price")return "Awaiting latest price";
  if(issue==="Current price unavailable")return "Latest price unavailable";
  if(issue==="Holdings and trades need a completed broker sync")return "Broker sync needed";
  if(issue.includes("Trade date"))return "Trade date missing";
  return issue || "Calculation unavailable";
}

// Read-only arithmetic over the already authorized account/holding subscriptions.
export function reconcileInvestmentAccount(account:{id:string;currentBalance?:number|null}, holdings:Array<{accountId:string;institutionValue:number;quantity:number;marketPrice?:number;institutionPrice:number;marketValue?:number}>, cash:(holding:any)=>boolean){
  const rows=holdings.filter(row=>row.accountId===account.id);
  const finite=(value:unknown)=>typeof value==="number"&&Number.isFinite(value);
  const complete=rows.length>0&&rows.every(row=>finite(row.institutionValue));
  const imported=complete?rows.reduce((sum,row)=>sum+row.institutionValue,0):null;
  const cashRows=rows.filter(cash),cashValue=complete?cashRows.reduce((sum,row)=>sum+row.institutionValue,0):null;
  const markedRows=rows.map(row=>{const price=row.marketPrice??row.institutionPrice;return finite(price)&&finite(row.quantity)?price*row.quantity:NaN;});
  const marked=rows.length>0&&markedRows.every(finite)?markedRows.reduce((sum,value)=>sum+value,0):null;
  const balance=finite(account.currentBalance)?account.currentBalance!:null;
  const adjustment=balance!==null&&imported!==null?balance-imported:null;
  return {count:rows.length,balance,imported,cashValue,marked,adjustment,cashRows,estimated:marked!==null&&adjustment!==null?marked+adjustment:null};
}
export function diagnosticTime(value:unknown){
  try {const v=value as {toDate?:()=>Date};const date=typeof v?.toDate==="function"?v.toDate():typeof value==="string"?new Date(value):null;return date&&Number.isFinite(date.getTime())?date.toISOString():null;}catch{return null;}
}

/** Sum only the selected accounts; never present an incomplete balance as a total. */
export function reportedInvestmentBalance(accounts: Array<{currentBalance?: number | null}>): number | null {
  if (!accounts.length || accounts.some(account => typeof account.currentBalance !== "number" || !Number.isFinite(account.currentBalance))) return null;
  const total = accounts.reduce((sum, account) => sum + account.currentBalance!, 0);
  return Number.isFinite(total) ? total : null;
}

/** Price imported positions only; balance residuals are not cash positions. */
export function refreshedInvestmentValue(accounts: Array<{id:string}>, holdings: Array<{accountId:string;quantity:number;marketPrice?:number;institutionPrice:number;institutionValue?:number;dayChangeStatus?:string}>): number | null {
  if (!accounts.length || accounts.some(a => !holdings.some(h => h.accountId === a.id))) return null;
  const rows = holdings.filter(h => accounts.some(a => a.id === h.accountId));
  const values = rows.map(h => holdingValue({...h,institutionValue:h.institutionValue??NaN}));
  if (values.some(v => v===null)) return null;
  const total = values.reduce<number>((sum, v) => sum + v!, 0);
  return Number.isFinite(total) ? total : null;
}

/** A pending gain does not erase a complete set of recorded opening values. */
export function recordedOpeningValue(accounts: Array<{positions: Array<{openingValue:number|null}>}>): number | null {
  if (!accounts.length) return null;
  const values=accounts.flatMap(account=>account.positions.map(position=>position.openingValue));
  if (values.some(value=>typeof value!=="number" || !Number.isFinite(value))) return null;
  const total=values.reduce<number>((sum,value)=>sum+value!,0);
  return Number.isFinite(total)?total:null;
}

/** Show saved calculation blockers without inferring a cause from an empty ranking. */
export function performanceBlockerSummary(accounts: Pick<InvestmentPerformanceAccount,"complete"|"issues">[]) {
  const issues=[...new Set(accounts.filter(account=>!account.complete).flatMap(account=>account.issues))];
  const labels=[...new Set(issues.map(issue=>issue==="Prior-week closing price unavailable"?"Opening price unavailable":issueLabel(issue)))];
  return labels.length?labels.join(" · "):accounts.some(account=>!account.complete)?"Calculation incomplete":"";
}
